import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useState, FormEvent } from "react";

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      if (typeof value === "string") data.append(key, value);
    });
    try {
      await fetch("https://readdy.ai/api/form/d75vnl7lpdqvlupfpmh0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-brand-black min-h-screen font-sans">
      <Navbar />
      <main className="pt-20">

        {/* Page Header */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://lemonprotector.com/images/home/slide2.jpg"
              alt="Schedule a pre-purchase car inspection — Lemon Protector"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-brand-black/87" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
            <p className="font-sans text-xs tracking-widest uppercase text-brand-gold mb-4 flex items-center justify-center gap-3">
              <span className="w-8 h-px bg-brand-gold" />
              Get Started
            </p>
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-brand-white leading-tight mb-4">
              Schedule Your 450-Point Inspection
            </h1>
            <p className="font-sans text-base text-brand-gray-light max-w-xl mx-auto">
              Fill out the form below and we&apos;ll get back to you promptly. Or call us directly — we&apos;re ready to help.
            </p>
            <div className="flex items-center justify-center gap-6 mt-6 flex-wrap">
              <a href="tel:5169833800" className="inline-flex items-center gap-2 text-brand-gold font-semibold font-sans text-sm hover:text-brand-gold-light transition-colors">
                <i className="ri-phone-fill" /> (516) 983-3800
              </a>
              <span className="text-brand-gold/30">|</span>
              <a href="tel:7182297594" className="inline-flex items-center gap-2 text-brand-gold font-semibold font-sans text-sm hover:text-brand-gold-light transition-colors">
                <i className="ri-phone-fill" /> (718) 229-7594
              </a>
            </div>
          </div>
        </section>

        {/* Form + Info */}
        <section className="py-20 lg:py-28">
          <div className="max-w-6xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-3 gap-12">

            {/* Left info */}
            <div className="lg:col-span-1 flex flex-col gap-8">
              <div>
                <h2 className="font-serif text-2xl font-semibold text-brand-white mb-4">What to Expect</h2>
                <ul className="flex flex-col gap-4">
                  {[
                    { icon: "ri-checkbox-circle-line", text: "450-point bumper-to-bumper inspection" },
                    { icon: "ri-file-text-line", text: "Detailed written report with photos emailed to you" },
                    { icon: "ri-history-line", text: "Free title search & vehicle history report included" },
                    { icon: "ri-user-voice-line", text: "Your actual inspector calls you with findings" },
                    { icon: "ri-price-tag-3-line", text: "Starting at $139 — no hidden fees" },
                    { icon: "ri-map-pin-2-line", text: "On-site at the vehicle's location — NY, NJ, CT" },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                        <i className={`${item.icon} text-brand-gold text-base`} />
                      </div>
                      <p className="font-sans text-sm text-brand-gray-light leading-relaxed">{item.text}</p>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-brand-charcoal border border-brand-gold/15 rounded-2xl p-6">
                <h3 className="font-serif text-lg font-semibold text-brand-white mb-3">Call Us Directly</h3>
                <p className="font-sans text-sm text-brand-gray mb-4">We&apos;re available to answer questions and schedule your inspection by phone.</p>
                <a href="tel:5169833800" className="block font-sans text-brand-gold font-bold text-lg hover:text-brand-gold-light transition-colors">(516) 983-3800</a>
                <a href="tel:7182297594" className="block font-sans text-brand-gold font-bold text-lg hover:text-brand-gold-light transition-colors mt-1">(718) 229-7594</a>
              </div>

              <div className="mt-2">
                <a
                  href="http://www.bbb.org/new-york-city/business-reviews/auto-diagnostic-service/lemon-protector-in-plainview-ny-8459/"
                  rel="nofollow"
                  target="_blank"
                >
                  <img
                    src="https://lemonprotector.com/images/home/blue-seal-63-134-lemon-protector-8459.png"
                    alt="BBB Accredited Business — Lemon Protector"
                    className="w-20 object-contain"
                  />
                </a>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2 bg-brand-charcoal border border-brand-gold/15 rounded-3xl p-8 lg:p-10">
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center gap-5">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-brand-gold/15 border border-brand-gold/30">
                    <i className="ri-check-line text-brand-gold text-3xl" />
                  </div>
                  <h3 className="font-serif text-2xl text-brand-white">Request Received!</h3>
                  <p className="font-sans text-sm text-brand-gray max-w-sm">
                    Thank you — we&apos;ll be in touch shortly to confirm your inspection details. Or call us at <a href="tel:5169833800" className="text-brand-gold">(516) 983-3800</a>.
                  </p>
                </div>
              ) : (
                <>
                  <h2 className="font-serif text-2xl font-semibold text-brand-white mb-2">Schedule an Inspection</h2>
                  <p className="font-sans text-sm text-brand-gray mb-7">Provide your vehicle and contact details and we&apos;ll coordinate the rest.</p>

                  <form
                    id="schedule-inspection-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="font-sans text-xs text-brand-gray-light uppercase tracking-wider mb-2 block">Your Name *</label>
                        <input
                          name="name"
                          type="text"
                          required
                          placeholder="Full name"
                          className="w-full bg-brand-dark border border-brand-gold/20 rounded-xl px-4 py-3 font-sans text-sm text-brand-white placeholder-brand-gray focus:outline-none focus:border-brand-gold/60 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="font-sans text-xs text-brand-gray-light uppercase tracking-wider mb-2 block">Phone Number *</label>
                        <input
                          name="phone"
                          type="tel"
                          required
                          placeholder="(xxx) xxx-xxxx"
                          className="w-full bg-brand-dark border border-brand-gold/20 rounded-xl px-4 py-3 font-sans text-sm text-brand-white placeholder-brand-gray focus:outline-none focus:border-brand-gold/60 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="font-sans text-xs text-brand-gray-light uppercase tracking-wider mb-2 block">Email Address</label>
                      <input
                        name="email"
                        type="email"
                        placeholder="your@email.com"
                        className="w-full bg-brand-dark border border-brand-gold/20 rounded-xl px-4 py-3 font-sans text-sm text-brand-white placeholder-brand-gray focus:outline-none focus:border-brand-gold/60 transition-colors"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                      <div>
                        <label className="font-sans text-xs text-brand-gray-light uppercase tracking-wider mb-2 block">Vehicle Year</label>
                        <input
                          name="vehicle_year"
                          type="text"
                          placeholder="e.g. 2018"
                          className="w-full bg-brand-dark border border-brand-gold/20 rounded-xl px-4 py-3 font-sans text-sm text-brand-white placeholder-brand-gray focus:outline-none focus:border-brand-gold/60 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="font-sans text-xs text-brand-gray-light uppercase tracking-wider mb-2 block">Make</label>
                        <input
                          name="vehicle_make"
                          type="text"
                          placeholder="e.g. Toyota"
                          className="w-full bg-brand-dark border border-brand-gold/20 rounded-xl px-4 py-3 font-sans text-sm text-brand-white placeholder-brand-gray focus:outline-none focus:border-brand-gold/60 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="font-sans text-xs text-brand-gray-light uppercase tracking-wider mb-2 block">Model</label>
                        <input
                          name="vehicle_model"
                          type="text"
                          placeholder="e.g. Camry"
                          className="w-full bg-brand-dark border border-brand-gold/20 rounded-xl px-4 py-3 font-sans text-sm text-brand-white placeholder-brand-gray focus:outline-none focus:border-brand-gold/60 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="font-sans text-xs text-brand-gray-light uppercase tracking-wider mb-2 block">Vehicle Location (City/State) *</label>
                      <input
                        name="vehicle_location"
                        type="text"
                        required
                        placeholder="Where is the car located?"
                        className="w-full bg-brand-dark border border-brand-gold/20 rounded-xl px-4 py-3 font-sans text-sm text-brand-white placeholder-brand-gray focus:outline-none focus:border-brand-gold/60 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="font-sans text-xs text-brand-gray-light uppercase tracking-wider mb-2 block">Additional Notes</label>
                      <textarea
                        name="notes"
                        placeholder="Any details about the vehicle or your schedule..."
                        rows={3}
                        maxLength={500}
                        className="w-full bg-brand-dark border border-brand-gold/20 rounded-xl px-4 py-3 font-sans text-sm text-brand-white placeholder-brand-gray focus:outline-none focus:border-brand-gold/60 transition-colors resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-4 bg-brand-gold text-brand-black font-sans font-bold text-sm tracking-wide rounded-full hover:bg-brand-gold-light transition-all disabled:opacity-60 whitespace-nowrap cursor-pointer"
                    >
                      {submitting ? "Sending..." : "Submit Inspection Request"}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
